import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { LegalFooter } from "@/components/LegalFooter";
import { toast } from "sonner";
import { BookOpen, Clock, ShieldCheck, ChevronRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopEbookDetailPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/ebooks/public/${slug}`)
      .then((r) => setEbook(r.data))
      .catch(() => setEbook(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const buy = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ebooks/checkout`, {
        slug,
        email: email.trim(),
        origin_url: window.location.origin,
        lang,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">…</div>;
  }
  if (!ebook) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-white/60 mb-4">Not found</div>
          <Link to="/shop/ebooks" className="hq-btn hq-btn-primary">← Back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="ebook-detail-page">
      <header className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-4">
        <Link to="/shop/ebooks" className="font-mono text-[10px] uppercase text-white/40 hover:text-white">
          ← {t(lang, "ebooks_all")}
        </Link>
      </header>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid gap-8 lg:grid-cols-[minmax(0,340px)_1fr]">
        <div
          className="aspect-[3/4] rounded-lg p-6 flex flex-col justify-between"
          style={{
            background: `linear-gradient(135deg, ${ebook.cover_hex || "#0E7C86"} 0%, #050505 130%)`,
          }}
        >
          <div className="font-mono text-[9px] uppercase tracking-widest text-white/70">
            EMRKT.CH Guide
          </div>
          <div>
            <div className="text-white font-display text-2xl leading-tight">{ebook.title}</div>
            {ebook.subtitle && (
              <div className="mt-2 text-white/80 text-xs italic">{ebook.subtitle}</div>
            )}
          </div>
          <div className="text-white/60 text-[10px] font-mono uppercase">{ebook.author_persona}</div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="text-white/50 font-mono text-[10px] uppercase mb-1">Guia digital</div>
            <h1 className="font-display font-light text-3xl sm:text-4xl tracking-tight">{ebook.title}</h1>
            {ebook.subtitle && (
              <p className="mt-3 text-white/70 italic">{ebook.subtitle}</p>
            )}
            <div className="mt-4 flex gap-4 text-white/60 text-xs font-mono uppercase">
              <span className="flex items-center gap-1"><BookOpen size={12} /> {ebook.chapter_count} capítulos</span>
              {ebook.reading_minutes && (
                <span className="flex items-center gap-1"><Clock size={12} /> {ebook.reading_minutes} min</span>
              )}
              <span className="flex items-center gap-1"><ShieldCheck size={12} /> PDF descarregável</span>
            </div>
          </div>

          {ebook.preview && (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
              <div className="text-white/40 font-mono text-[10px] uppercase mb-2">Preview</div>
              <p className="text-white/80 text-sm italic leading-relaxed">{ebook.preview}</p>
            </div>
          )}

          {ebook.chapter_titles?.length ? (
            <div>
              <div className="text-white/40 font-mono text-[10px] uppercase mb-3">Índice</div>
              <ol className="space-y-2">
                {ebook.chapter_titles.map((ct, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-[color:var(--hq-cyan)] font-mono text-xs pt-0.5">{String(i+1).padStart(2,"0")}</span>
                    <span>{ct}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div className="rounded-lg border border-[color:var(--hq-cyan)]/30 bg-[color:var(--hq-cyan)]/[0.06] p-5">
            <div className="flex items-baseline justify-between mb-4">
              <div className="font-display text-2xl">CHF {(ebook.price_chf ?? 0).toFixed(2)}</div>
              <div className="text-[10px] font-mono uppercase text-white/50">entrega imediata por email</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="ebook-buy-email"
                type="email"
                placeholder={t(lang, "ebooks_email_placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hq-input flex-1 text-sm"
              />
              <button
                data-testid="ebook-buy-btn"
                onClick={buy}
                disabled={busy || !email.trim()}
                className="hq-btn hq-btn-primary disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
                {t(lang, "ebooks_buy_now")}
              </button>
            </div>
            <div className="mt-2 font-mono text-[10px] uppercase text-white/40">
              {t(lang, "ebooks_digital_disclaimer")}
            </div>
          </div>
        </div>
      </section>
      <LegalFooter />
    </div>
  );
}
